// Rules for the "turn on desktop notifications" banner (AuthedLayout).
//
// Kept free of imports (and of any browser globals) so the root vitest suite
// can load it directly — same pattern as notification-categories.ts.
//
// Browsers only let a page request notification permission from a user
// gesture, so the app can't switch desktop notifications on by itself. This
// decides when to ask the person to do it.

export type BrowserPermission = 'default' | 'granted' | 'denied' | 'unsupported';

/** What the banner should offer, or null to stay hidden. */
export type BannerMode =
  /** Browser permission not asked yet → "Enable notifications" runs the prompt. */
  | 'allow'
  /** Permission granted but the in-app switch is off → "Turn on" flips the switch. */
  | 'turn-on'
  | null;

/** "Not now" hides the banner for a week, then it comes back once. */
export const BANNER_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

export const BANNER_DISMISS_KEY = 'pa-desktop-notif-banner-dismissed-until-v1';

/** Parse the stored epoch-ms "dismissed until" value. Anything missing,
 *  non-numeric or non-positive counts as "never dismissed" (0). */
export function parseDismissedUntil(raw: string | null | undefined): number {
  if (!raw) return 0;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function bannerMode(args: {
  permission: BrowserPermission;
  /** The in-app master switch (notification-prefs `enabled`). */
  enabled: boolean;
  /** Epoch ms until which the person asked not to be nagged; 0 = not dismissed. */
  dismissedUntil: number;
  now: number;
}): BannerMode {
  const { permission, enabled, dismissedUntil, now } = args;
  // Can't help: no API, or the person blocked the site — re-asking is
  // impossible from code and nagging would be hostile. Settings explains
  // how to unblock.
  if (permission === 'unsupported' || permission === 'denied') return null;
  if (dismissedUntil > now) return null;
  if (permission === 'default') return 'allow';
  // granted
  return enabled ? null : 'turn-on';
}
